import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService, StateUtils } from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, share, switchMap, take } from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth';
import { ArticleService } from '../../../../core/catalog/article/services';
import { Article, CartType, OrderEntry, ReelCutting, ReelCuttingOptions, ReelCuttingRequest } from '../../../../core/model';
import { PrincipalConfigurationService } from '../../../../core/user';
import { withdrawOn } from '../../../../core/util/rxjs';
import { makeReelCuttingKey } from '../../../../shared';
import { ActiveCartFacade, ReelCuttingFacade } from '../facades';
import { CartActions } from '../store/actions';
import { StateWithCart } from '../store/cart-state';
import { CartSelectors } from '../store/selectors';

@Injectable()
export class ReelCuttingService implements ReelCuttingFacade {
  constructor(
    private store: Store<StateWithCart>,
    private authService: AuthService,
    private userIdService: UserIdService,
    private activeCartService: ActiveCartFacade,
    private articleService: ArticleService,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}

  getOptions(entry: OrderEntry, cartType: CartType): Observable<ReelCuttingOptions> {
    this.assertStockCart(cartType);

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) => {
        return combineLatest([
          of(entry),
          this.articleService.getArticle(entry.articleRef),
          this.principalConfigurationService.getValues(),
        ]);
      }),
      map(([entry, article, configValues]) => {
        const options: ReelCuttingOptions = {
          enabled: configValues['enableReelCutting']?.value as boolean,
          lengthCutting: {
            enabled: false,
          },
          widthCutting: {
            enabled: false,
          },
        };
        if (options.enabled) {
          if (
            (configValues['enableReelLengthCutting']?.value as boolean) &&
            this.isReelConfiguredForLengthCutting(article) &&
            this.doesQuantityRequireLengthCutting(entry, article)
          ) {
            options.lengthCutting = {
              enabled: true,
              minimumLength: article.deliveryQuantity,
            };
          }
          if ((configValues['enableReelWidthCutting']?.value as boolean) && article.cuttable) {
            options.widthCutting = {
              enabled: true,
              maxNumberOfTargetReels: configValues['reelCuttingMaxNumberOfTargetReels']?.value as number,
              minimumMargin: configValues['reelCuttingMinimumMargin']?.value as number,
              cutWidth: configValues['reelCuttingCutWidth']?.value as number,
              lastTargetReelMinimumWidth: configValues['reelCuttingLastTargetReelMinimumWidth']?.value as number,
            };
          }
        }
        return options;
      }),
      take(1)
    );
  }

  private isReelConfiguredForLengthCutting(article: Article): boolean {
    return (
      article.deliveryQuantity !== undefined &&
      ((article.deliveryUnit === undefined && article.unit.code === 'M') || article.deliveryUnit === 'M')
    );
  }

  private doesQuantityRequireLengthCutting(entry: OrderEntry, article: Article): boolean {
    const inEcommerceQuantity = entry.quantity * entry.unit?.inEcommerceUnit;
    return inEcommerceQuantity <= this.getReelLength(article);
  }

  private getReelLength(article: Article): number {
    return article.classificationAttributes['ZZRLL'] ? parseInt(article.classificationAttributes['ZZRLL'].value, 10) : 0;
  }

  private loadPrice(cartType: CartType, cartId: string, entry: OrderEntry, request: ReelCuttingRequest, key: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new CartActions.CalculateReelCuttingPrice({
            cartType,
            cartId,
            userId,
            entry,
            reelCutting: request,
            key,
          })
        ),
      () => {}
    );
  }

  calculatePrice(entry: OrderEntry, request: ReelCuttingRequest, cartType: CartType): Observable<ReelCutting | undefined> {
    this.assertStockCart(cartType);

    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) => {
        const key = makeReelCuttingKey({
          articleCode: entry.articleRef,
          quantity: entry.quantity,
          unit: entry.unit,
          reelCutting: request,
        });
        return of(key);
      }),
      switchMap((key) => {
        return combineLatest([
          of(key),
          this.store.select(CartSelectors.getReelCuttingPriceStateFactory(key)),
          isUserLoggedIn$,
          this.activeCartService.getActiveCartId(cartType),
        ]);
      }),
      filter(([key, loaderState, isUserLoggedIn, cartId]) => {
        if (isUserLoggedIn && this.needsToLoad(loaderState)) {
          this.loadPrice(cartType, cartId, entry, request, key);
          return false;
        }
        return loaderState.success;
      }),
      map(([_key, loaderState, _isUserLoggedIn, _cart]) => loaderState.value),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn))),
      take(1)
    );
  }

  calculatePriceLoading(entry: OrderEntry, request: ReelCuttingRequest, cartType: CartType): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeReelCuttingKey({
            articleCode: entry.articleRef,
            quantity: entry.quantity,
            unit: entry.unit,
            reelCutting: request,
          })
        )
      ),
      switchMap((key) => this.store.select(CartSelectors.getReelCuttingPriceLoadingFactory(key)))
    );
  }

  calculatePriceLoaded(entry: OrderEntry, request: ReelCuttingRequest, cartType: CartType): Observable<boolean> {
    this.assertStockCart(cartType);

    return this.activeCartService.getEntryById(entry.id, cartType).pipe(
      filter((entry) => entry !== undefined),
      switchMap((entry) =>
        of(
          makeReelCuttingKey({
            articleCode: entry.articleRef,
            quantity: entry.quantity,
            unit: entry.unit,
            reelCutting: request,
          })
        )
      ),
      switchMap((key) => this.store.select(CartSelectors.getReelCuttingPriceLoadedFactory(key)))
    );
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<ReelCutting>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }

  private assertStockCart(cartType: CartType) {
    if (cartType !== CartType.stock) {
      throw new Error('Reel cutting is only possible for stock carts');
    }
  }
}
